import React, { useState } from 'react';
import { ComprehensiveScenario, ScenarioDependency, DependencyType } from '../../types/scenarios';
import { useScenarios } from '../../hooks/useScenarios';
import { useVisibleScenarios } from '../../hooks/useVisibleScenarios';
import { useApp } from '../../contexts/AppContext';
import { usePermissions } from '../../hooks/usePermissions';
import * as scenarioApi from '../../api/services/scenarioService';
import { useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import { Plus, X, ArrowRight, AlertCircle, CheckCircle, Link2, Zap } from 'lucide-react';

interface DependenciesTabProps {
  scenario: ComprehensiveScenario;
  onUpdate: (updates: Partial<ComprehensiveScenario>) => void;
  readOnly?: boolean;
}

export const DependenciesTab: React.FC<DependenciesTabProps> = ({ scenario, onUpdate, readOnly = false }) => {
  const { updateScenario } = useScenarios();
  const scenarios = useVisibleScenarios();
  const { mode } = useApp();
  const { hasPermission } = usePermissions();
  const isDemo = mode === 'demo';
  const canEditDependencies = !readOnly && hasPermission('edit_system_scenarios');
  const queryClient = useQueryClient();
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [selectedScenarioId, setSelectedScenarioId] = useState<string>('');
  const [selectedType, setSelectedType] = useState<DependencyType>('prerequisite');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

  // Get current dependencies as array
  const getDependencies = (): ScenarioDependency[] => {
    if (!scenario.dependencies) return [];
    if (Array.isArray(scenario.dependencies)) return scenario.dependencies;
    
    // Convert structured format to array
    const deps: ScenarioDependency[] = [];
    if (scenario.dependencies.prerequisites) {
      scenario.dependencies.prerequisites.forEach((prereq, idx) => {
        deps.push({
          id: `prereq-${idx}`,
          type: 'prerequisite',
          targetScenarioId: prereq.description, // This is a placeholder
          isMet: prereq.met,
        });
      });
    }
    return deps;
  };

  const dependencies = getDependencies();

  // Get available scenarios (exclude current scenario and already linked ones)
  const availableScenarios = scenarios.filter(s => {
    if (s.id === scenario.id) return false;
    const linkedIds = dependencies.map(d => d.targetScenarioId);
    return !linkedIds.includes(s.id);
  });

  // Add dependency with two-way linking
  const handleAddDependency = () => {
    if (!selectedScenarioId) return;

    if (!isDemo) {
      // Live mode: call API (backend handles two-way linking atomically)
      const numericScenarioId = parseInt(scenario.id, 10);
      const numericTargetId = parseInt(selectedScenarioId, 10);
      scenarioApi.createScenarioDependency(numericScenarioId, {
        targetScenarioId: numericTargetId,
        dependencyType: selectedType,
      }).then(() => {
        queryClient.invalidateQueries({ queryKey: ['scenarios'] });
      }).catch((error) => {
        console.error('Failed to create dependency:', error);
      });

      // Reset form
      setSelectedScenarioId('');
      setSelectedType('prerequisite');
      setAddDialogOpen(false);
      return;
    }

    // Demo mode: keep existing in-memory logic
    const newDependency: ScenarioDependency = {
      id: `dep-${Date.now()}`,
      type: selectedType,
      targetScenarioId: selectedScenarioId,
      isMet: false,
    };

    // Add to current scenario
    const updatedDependencies = [...dependencies, newDependency];
    
    // Add reverse dependency to target scenario (two-way linking)
    const targetScenario = scenarios.find(s => s.id === selectedScenarioId);
    if (targetScenario) {
      const reverseType = getReverseType(selectedType);
      const reverseDependency: ScenarioDependency = {
        id: `dep-${Date.now()}-reverse`,
        type: reverseType,
        targetScenarioId: scenario.id,
        isMet: false,
      };

      const targetDeps = Array.isArray(targetScenario.dependencies) 
        ? targetScenario.dependencies 
        : [];
      
      const updatedTargetDeps = [...targetDeps, reverseDependency];
      updateScenario(selectedScenarioId, { dependencies: updatedTargetDeps });
      
      setTimeout(() => {
        onUpdate({ dependencies: updatedDependencies });
      }, 10);
    } else {
      onUpdate({ dependencies: updatedDependencies });
    }

    // Reset form
    setSelectedScenarioId('');
    setSelectedType('prerequisite');
    setAddDialogOpen(false);
  };

  // Remove dependency with two-way unlinking
  const handleRemoveDependency = (dependencyId: string) => {
    const dependency = dependencies.find(d => d.id === dependencyId);
    if (!dependency) return;

    if (!isDemo) {
      // Live mode: call API (backend handles reverse cleanup)
      const numericScenarioId = parseInt(scenario.id, 10);
      const numericDependencyId = parseInt(dependencyId, 10);
      scenarioApi.deleteScenarioDependency(numericScenarioId, numericDependencyId).then(() => {
        queryClient.invalidateQueries({ queryKey: ['scenarios'] });
      }).catch((error) => {
        console.error('Failed to delete dependency:', error);
      });
      return;
    }

    // Demo mode: keep existing in-memory logic
    const updatedDependencies = dependencies.filter(d => d.id !== dependencyId);

    const targetScenario = scenarios.find(s => s.id === dependency.targetScenarioId);
    if (targetScenario) {
      const targetDeps = Array.isArray(targetScenario.dependencies) 
        ? targetScenario.dependencies 
        : [];
      
      const updatedTargetDeps = targetDeps.filter(d => d.targetScenarioId !== scenario.id);
      updateScenario(dependency.targetScenarioId, { dependencies: updatedTargetDeps });
      
      setTimeout(() => {
        onUpdate({ dependencies: updatedDependencies });
      }, 10);
    } else {
      onUpdate({ dependencies: updatedDependencies });
    }
  };

  // Get reverse dependency type for two-way linking
  const getReverseType = (type: DependencyType): DependencyType => {
    switch (type) {
      case 'prerequisite':
        return 'enables';
      case 'enables':
        return 'prerequisite';
      case 'conflict':
        return 'conflict';
      case 'enhanced-by':
        return 'enhanced-by';
      default:
        return type;
    }
  };

  // Get dependency type icon and color
  const getDependencyStyle = (type: DependencyType) => {
    switch (type) {
      case 'prerequisite':
        return {
          icon: AlertCircle,
          color: 'text-amber-600',
          bgColor: 'bg-amber-50',
          borderColor: 'border-amber-200',
          label: 'Prerequisite',
        };
      case 'conflict':
        return {
          icon: X,
          color: 'text-red-600',
          bgColor: 'bg-red-50',
          borderColor: 'border-red-200',
          label: 'Conflict',
        };
      case 'enables':
        return {
          icon: CheckCircle,
          color: 'text-green-600',
          bgColor: 'bg-green-50',
          borderColor: 'border-green-200',
          label: 'Enables',
        };
      case 'enhanced-by':
        return {
          icon: Zap,
          color: 'text-purple-600',
          bgColor: 'bg-purple-50',
          borderColor: 'border-purple-200',
          label: 'Enhanced By',
        };
      default:
        return {
          icon: Link2,
          color: 'text-gray-600',
          bgColor: 'bg-gray-50',
          borderColor: 'border-gray-200',
          label: type,
        };
    }
  };

  // Group dependencies by type
  const groupedDependencies = dependencies.reduce((acc, dep) => {
    if (!acc[dep.type]) acc[dep.type] = [];
    acc[dep.type].push(dep);
    return acc;
  }, {} as Record<DependencyType, ScenarioDependency[]>);

  return (
    <div className="space-y-6">
      {/* Header with Add Button */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Scenario Dependencies</h3>
          <p className="text-sm text-gray-600 mt-1">
            Link scenarios that depend on, conflict with, or enhance each other
          </p>
        </div>
        {canEditDependencies && (
          <Button onClick={() => setAddDialogOpen(true)} className="gap-2">
            <Plus className="h-4 w-4" />
            Add Dependency
          </Button>
        )}
      </div>

      {/* Dependencies List */}
      {dependencies.length === 0 ? (
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <Link2 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No dependencies yet</h3>
              <p className="text-sm text-gray-600 mb-4">
                Link this scenario to others to track relationships and impacts
              </p>
              {canEditDependencies && (
                <Button onClick={() => setAddDialogOpen(true)} variant="outline" className="gap-2">
                  <Plus className="h-4 w-4" />
                  Add First Dependency
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {Object.entries(groupedDependencies).map(([type, deps]) => {
            const style = getDependencyStyle(type as DependencyType);
            const Icon = style.icon;

            return (
              <Card key={type}>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Icon className={`h-5 w-5 ${style.color}`} />
                    {style.label}
                    {deps.length > 0 && <Badge variant="secondary">{deps.length}</Badge>}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {deps.map(dep => {
                      const targetScenario = scenarios.find(s => s.id === dep.targetScenarioId);
                      if (!targetScenario) return null;

                      return (
                        <div
                          key={dep.id}
                          className={`flex items-center justify-between p-4 rounded-lg border ${style.borderColor} ${style.bgColor}`}
                        >
                          <div className="flex items-center gap-3 flex-1">
                            <Icon className={`h-5 w-5 ${style.color}`} />
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-gray-900">{scenario.name}</span>
                              <ArrowRight className="h-4 w-4 text-gray-400" />
                              <span className="font-medium text-gray-900">{targetScenario.name}</span>
                            </div>
                            {targetScenario.status && (
                              <Badge variant="outline" className="ml-2">
                                {targetScenario.status}
                              </Badge>
                            )}
                          </div>
                          {canEditDependencies && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setPendingDeleteId(dep.id);
                                setDeleteDialogOpen(true);
                              }}
                              className="text-gray-500 hover:text-red-600"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Delete Dependency Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={() => {
        setDeleteDialogOpen(false);
        setPendingDeleteId(null);
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Dependency</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove this dependency
              {pendingDeleteId && (() => {
                const dep = dependencies.find(d => d.id === pendingDeleteId);
                const target = dep ? scenarios.find(s => s.id === dep.targetScenarioId) : null;
                return target ? <> to <span className="font-semibold">"{target.name}"</span></> : null;
              })()}
              ? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setDeleteDialogOpen(false);
              setPendingDeleteId(null);
            }}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (pendingDeleteId) {
                  handleRemoveDependency(pendingDeleteId);
                }
                setDeleteDialogOpen(false);
                setPendingDeleteId(null);
              }}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Add Dependency Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Dependency</DialogTitle>
            <DialogDescription>
              Link this scenario to another. A reverse link will be automatically created.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Dependency Type</label>
              <Select value={selectedType} onValueChange={(value) => setSelectedType(value as DependencyType)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="prerequisite">
                    <div className="flex items-center gap-2">
                      <AlertCircle className="h-4 w-4 text-amber-600" />
                      <span>Prerequisite</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="conflict">
                    <div className="flex items-center gap-2">
                      <X className="h-4 w-4 text-red-600" />
                      <span>Conflict</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="enables">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <span>Enables</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="enhanced-by">
                    <div className="flex items-center gap-2">
                      <Zap className="h-4 w-4 text-purple-600" />
                      <span>Enhanced By</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Target Scenario</label>
              <Select value={selectedScenarioId} onValueChange={setSelectedScenarioId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a scenario..." />
                </SelectTrigger>
                <SelectContent>
                  {availableScenarios.map(s => (
                    <SelectItem key={s.id} value={s.id}>
                      <div className="flex items-center gap-2">
                        <span>{s.name}</span>
                        {s.status && (
                          <Badge variant="outline" className="ml-2">
                            {s.status}
                          </Badge>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {selectedType && selectedScenarioId && (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-900">
                  <strong>Two-way linking:</strong> A reverse "{getReverseType(selectedType)}" dependency will be automatically added to the target scenario.
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddDependency} disabled={!selectedScenarioId}>
              Add Dependency
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
